<template>
    <section class="uses-tailwind px-5 h-[calc(100vh_-_120px)] text-lg">
        <loader
            v-if="isLoadingProduct || isSavingAndProcessingProduct"
            :cover="true"
            :message="
                isLoadingProduct
                    ? trans(
                          'cutting_pleating_validation.feedback.loading_details'
                      )
                    : trans(
                          'cutting_pleating_validation.feedback.saving_and_processing_product'
                      )
            "
        />
        <div
            v-if="!isLoadingProduct && product"
            class="flex flex-col h-full"
        >
            <div
                class="flex items-center justify-between flex-grow-0 flex-shrink-0 mb-5"
            >
                <div class="flex items-center gap-2">
                    <ui5-button
                        class="border border-primary mb-[3px] hover:text-primary"
                        v-on:click="navigateToOverview()"
                    >
                        {{
                            trans(
                                'cutting_pleating_validation.actions.back_to_overview'
                            )
                        }}
                    </ui5-button>
                    <ui5-button
                        :disabled="isFormDirty"
                        class="border border-primary mb-[3px] hover:text-primary"
                        @click="exportDxfJson()"
                    >
                        {{
                            trans(
                                'cutting_pleating_validation.actions.download_dxf_json'
                            )
                        }}
                    </ui5-button>
                </div>
                <div class="flex items-center gap-2">
                    <ui5-button
                        class="border border-primary mb-[3px] hover:text-primary"
                        v-on:click="navigateToPreviousProduct()"
                        v-bind:disabled="product.previousProductId === null"
                    >
                        {{
                            trans(
                                'cutting_pleating_validation.actions.previous_product'
                            )
                        }}
                    </ui5-button>
                    <ui5-button
                        class="border border-primary mb-[3px] hover:text-primary"
                        v-on:click="navigateToNextProduct()"
                        v-bind:disabled="product.nextProductId === null"
                    >
                        {{
                            trans(
                                'cutting_pleating_validation.actions.next_product'
                            )
                        }}
                    </ui5-button>
                </div>
            </div>
            <div class="grid grid-cols-12 divide-x divide-slate-400 flex-grow">
                <div
                    class="col-span-full md:col-span-6 md:order-2 lg:col-span-5 lg:order-1 xl:col-span-4 xl:order-1 px-4 py-2 order-3"
                >
                    <h2 class="font-bold text-xl mb-2">
                        {{
                            trans(
                                'cutting_pleating_validation.details.sections.configuration'
                            )
                        }}
                    </h2>
                    <div class="flex items-center justify-end mb-3">
                        <ui5-button
                            class="border border-primary mb-[3px] hover:text-primary"
                            v-on:click="storeValueFields()"
                            :disabled="!isFormDirty || isStoringValueFields"
                        >
                            {{
                                trans(
                                    'cutting_pleating_validation.actions.save_data_fields'
                                )
                            }}
                        </ui5-button>
                    </div>
                    <div>
                        <div
                            v-for="dataField in sortedDataFields"
                            :key="dataField.id"
                            class="mb-3 grid grid-cols-3 gap-x-2 items-start"
                        >
                            <ui5-label class="h-[36px] leading-[36px] text-lg">
                                {{ dataFieldLabel(dataField) }}
                            </ui5-label>
                            <div
                                class="col-span-2"
                                v-for="dataFieldIdentifier in [
                                    getDataFieldIdentifier(dataField),
                                ]"
                            >
                                <pre
                                    v-if="isDebug"
                                    style="font-size: 8px; line-height: normal"
                                    >{{
                                        {
                                            dataFieldIdentifier,
                                            datatype:
                                                dataField.datatype &&
                                                dataField.datatype.type
                                                    ? dataField.datatype.type
                                                    : 'no type',
                                            currentValue:
                                                form.valueFields[
                                                    dataFieldIdentifier
                                                ],
                                        }
                                    }}</pre
                                >
                                <ui5-select
                                    v-if="
                                        dataField.datatype &&
                                        [
                                            'options',
                                            'popup',
                                            'table',
                                            'multiselect',
                                        ].includes(dataField.datatype.type)
                                    "
                                    class="w-full"
                                    v-bind:value="
                                        form.valueFields[dataFieldIdentifier]
                                    "
                                    v-on:change="
                                        updateFormValueField(
                                            dataField,
                                            $event.target.value
                                        )
                                    "
                                    v-bind:multiple="
                                        dataField.datatype.type ===
                                        'multiselect'
                                    "
                                >
                                    <ui5-option
                                        v-if="
                                            dataField.datatype.type !=
                                            'multiselect'
                                        "
                                        :selected="
                                            form.valueFields[
                                                dataFieldIdentifier
                                            ] == null ||
                                            form.valueFields[
                                                dataFieldIdentifier
                                            ] == ''
                                        "
                                        :key="`datafield-${dataField.id}-null-selected`"
                                        :value="'null'"
                                    >
                                        ({{
                                            trans(
                                                'cutting_pleating_validation.errors.missing_value'
                                            )
                                        }})
                                    </ui5-option>
                                    <ui5-option
                                        v-for="option in dataField.options"
                                        :key="`datafield-${dataField.id}-option-${option.value}`"
                                        v-bind:value="option.value"
                                        v-bind:selected="
                                            form.valueFields[
                                                dataFieldIdentifier
                                            ] == option.value
                                        "
                                    >
                                        {{ option.label }}
                                    </ui5-option>
                                </ui5-select>
                                <date-input
                                    v-else-if="
                                        dataField.datatype &&
                                        ['date', 'datetime'].includes(
                                            dataField.datatype.type
                                        )
                                    "
                                    v-bind:type="dataField.datatype.type"
                                    v-bind:value="
                                        moment(
                                            form.valueFields[
                                                dataFieldIdentifier
                                            ]
                                        ).format(
                                            dataField.datatype.type === 'date'
                                                ? 'YYYY-MM-DD'
                                                : 'YYYY-MM-DD HH:mm:ss'
                                        )
                                    "
                                    v-on:change="
                                        updateFormValueField(
                                            dataField,
                                            $event.detail.value
                                        )
                                    "
                                />
                                <ui5-input
                                    v-else
                                    class="w-full"
                                    v-bind:value="
                                        form.valueFields[dataFieldIdentifier]
                                    "
                                    v-on:change="
                                        updateFormValueField(
                                            dataField,
                                            $event.target.value
                                        )
                                    "
                                />
                            </div>
                        </div>
                    </div>
                    <div class="flex items-center justify-end mt-3">
                        <ui5-button
                            class="border border-primary mb-[3px] hover:text-primary"
                            v-on:click="storeValueFields()"
                            :disabled="!isFormDirty || isStoringValueFields"
                        >
                            {{
                                trans(
                                    'cutting_pleating_validation.actions.save_data_fields'
                                )
                            }}
                        </ui5-button>
                    </div>
                </div>
                <div
                    class="col-span-full md:order-1 lg:col-span-7 lg:order-2 xl:col-span-5 xl:order-2 px-4 py-2 order-1 mb-5 lg:mb-0"
                >
                    <h2 class="font-bold text-xl mb-2">
                        {{ productTitle }}
                    </h2>
                    <div class="text-lg mb-2">
                        {{ product.customerName }}
                    </div>
                    <div
                        class="aspect-video bg-slate-100 mb-5 cursor-pointer relative"
                    >
                        <img
                            v-on:click="toggleImageModal(true)"
                            v-bind:src="product.imageSrc"
                            crossorigin="anonymous"
                            @load="hasValidCustomProductImage = true"
                            @error="hasValidCustomProductImage = false"
                            class="object-contain w-full h-full"
                        />
                    </div>
                    <div class="flex gap-4 mb-5 justify-end">
                        <ui5-button
                            class="border border-primary hover:text-primary flex items-center justify-center gap-1"
                            v-on:click="toggleImageSelectModal(true)"
                        >
                            <i class="fa fa-upload"></i>
                            {{
                                trans(
                                    'cutting_pleating_validation.actions.upload_image'
                                )
                            }}
                        </ui5-button>
                        <ui5-button
                            v-if="hasValidCustomProductImage"
                            class="border border-primary hover:text-primary flex items-center justify-center gap-1"
                            v-on:click="downloadImage"
                        >
                            <i class="fa fa-download"></i>
                            {{
                                trans(
                                    'cutting_pleating_validation.actions.download_image'
                                )
                            }}
                        </ui5-button>
                    </div>
                    <h2 class="font-bold text-xl mb-2">
                        {{
                            trans(
                                'cutting_pleating_validation.details.sections.deduction_values'
                            )
                        }}
                    </h2>
                    <div>
                        <ul>
                            <li
                                v-for="deductionValue in deductionValues"
                                :key="deductionValue.id"
                                class="leading-[36px]"
                            >
                                <span class="font-bold">
                                    {{ deductionValue.label }}:
                                </span>
                                <span>{{ deductionValue.value }}</span>
                            </li>
                            <li class="leading-[36px]">
                                <span class="font-bold block w-full">
                                    {{
                                        trans(
                                            'cutting_pleating_validation.details.labels.used_angle_deductions'
                                        )
                                    }}:
                                </span>
                                <table
                                    class="table table-auto w-full max-w-full text-sm mb-5"
                                >
                                    <thead class="border-b border-slate-800">
                                        <tr>
                                            <th class="px-2 py-2 font-bold">
                                                {{
                                                    trans(
                                                        'cutting_pleating_validation.used_angle_deductions_table.columns.corner'
                                                    )
                                                }}
                                            </th>
                                            <th class="px-2 py-2 font-bold">
                                                {{
                                                    trans(
                                                        'cutting_pleating_validation.used_angle_deductions_table.columns.forming_press'
                                                    )
                                                }}
                                            </th>
                                            <th class="px-2 py-2 font-bold">
                                                {{
                                                    trans(
                                                        'cutting_pleating_validation.used_angle_deductions_table.columns.stamp'
                                                    )
                                                }}
                                            </th>
                                            <th class="px-2 py-2 font-bold">
                                                {{
                                                    trans(
                                                        'cutting_pleating_validation.used_angle_deductions_table.columns.stamp_die'
                                                    )
                                                }}
                                            </th>
                                            <th class="px-2 py-2 font-bold">
                                                {{
                                                    trans(
                                                        'cutting_pleating_validation.used_angle_deductions_table.columns.thickness'
                                                    )
                                                }}
                                            </th>
                                            <th class="px-2 py-2 font-bold">
                                                {{
                                                    trans(
                                                        'cutting_pleating_validation.used_angle_deductions_table.columns.angle'
                                                    )
                                                }}
                                            </th>
                                            <th class="px-2 py-2 font-bold">
                                                {{
                                                    trans(
                                                        'cutting_pleating_validation.used_angle_deductions_table.columns.value'
                                                    )
                                                }}
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr
                                            v-if="
                                                !product.usedAngleDeductions ||
                                                product.usedAngleDeductions
                                                    .length === 0
                                            "
                                        >
                                            <td
                                                colspan="7"
                                                class="px-2 py-2 font-light text-center"
                                            >
                                                {{ trans('general.no_data') }}
                                            </td>
                                        </tr>
                                        <tr
                                            v-for="usedAngleDeduction in product.usedAngleDeductions"
                                        >
                                            <td class="px-2 py-2">
                                                {{
                                                    dataFieldLabel(
                                                        usedAngleDeduction.dataField
                                                    )
                                                }}
                                                {{
                                                    getValueFieldValue(
                                                        usedAngleDeduction.dataField
                                                    )
                                                }}
                                            </td>
                                            <td class="px-2 py-2">
                                                {{
                                                    usedAngleDeduction.formingPress
                                                }}
                                            </td>
                                            <td class="px-2 py-2">
                                                {{ usedAngleDeduction.stamp }}
                                            </td>
                                            <td class="px-2 py-2">
                                                {{
                                                    usedAngleDeduction.stampDie
                                                }}
                                            </td>
                                            <td class="px-2 py-2">
                                                {{
                                                    usedAngleDeduction.thickness
                                                }}&nbsp;mm
                                            </td>
                                            <td class="px-2 py-2">
                                                {{ usedAngleDeduction.angle }}˚
                                            </td>
                                            <td class="px-2 py-2">
                                                {{
                                                    usedAngleDeduction.value
                                                }}&nbsp;mm
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </li>
                            <li class="leading-[36px] flex items-center">
                                <span
                                    class="font-bold flex-grow-0 flex-shrink-0"
                                >
                                    {{
                                        trans(
                                            'cutting_pleating_validation.details.labels.unfoldment_after_calculation'
                                        )
                                    }}:
                                </span>
                                <text-input
                                    class="flex-grow text-end"
                                    type="number"
                                    v-bind:placeholder="
                                        String(product.calculatedUnfoldment)
                                    "
                                    v-model="manualUnfoldment"
                                />
                                <span class="flex-grow-0 flex-shrink-0">
                                    mm
                                </span>
                                <ui5-button
                                    class="border border-primary mb-[3px] hover:text-primary"
                                    @click="copyManualToDataField()"
                                >
                                    ↞
                                </ui5-button>
                            </li>
                        </ul>
                    </div>
                </div>

                <div
                    class="col-span-full md:order-3 md:col-span-6 lg:col-span-4 lg:order-3 xl:col-span-3 xl:order-3 px-4 py-2 order-2 mb-5 xl:mb-0"
                >
                    <h2 class="font-bold text-xl mb-2">
                        {{
                            trans(
                                'cutting_pleating_validation.details.sections.production_planning'
                            )
                        }}
                    </h2>
                    <ul>
                        <li class="leading-[36px]">
                            <span class="font-bold">
                                {{
                                    trans(
                                        'cutting_pleating_validation.details.labels.delivery_info'
                                    )
                                }}:
                            </span>
                            <span v-bind:class="deliveryTimeColorClasses">
                                {{ deliveryTimeLabel }}
                                {{
                                    product.requested_delivery_date
                                        ? `(${product.requested_delivery_date})`
                                        : null
                                }}
                            </span>
                        </li>
                        <li class="leading-[36px]">
                            <span class="font-bold">
                                {{
                                    trans(
                                        'cutting_pleating_validation.details.labels.attachments'
                                    )
                                }}:
                            </span>
                            <ul
                                class="space-y-2"
                                v-if="
                                    product.attachments &&
                                    product.attachments.length
                                "
                            >
                                <li
                                    v-for="attachment in product.attachments"
                                    :key="attachment.id"
                                >
                                    <div
                                        @click="downloadAttachment(attachment)"
                                        class="border border-slate-400 py-1 px-2 hover:shadow transition-all bg-white cursor-pointer flex items-center justify-between"
                                    >
                                        <span>{{ attachment.fileName }}</span>
                                        <i class="fa fa-download" />
                                    </div>
                                </li>
                                <li>
                                    <div
                                        @click="downloadAllProductAttachments()"
                                        class="block w-full border border-slate-400 py-1 px-2 hover:shadow transition-all bg-white cursor-pointer text-center text-primary"
                                    >
                                        <span>
                                            {{
                                                trans(
                                                    'cutting_pleating_validation.actions.download_all_attachments'
                                                )
                                            }}
                                        </span>
                                    </div>
                                </li>
                            </ul>
                            <p
                                v-else
                                class="text-center text-base font-light px-2 py-1"
                            >
                                {{
                                    trans(
                                        'cutting_pleating_validation.feedback.no_attachments'
                                    )
                                }}
                            </p>
                        </li>
                    </ul>
                    <div class="mt-5 flex items-center flex-col">
                        <ui5-button
                            class="border border-primary mb-[3px] hover:text-primary w-full"
                            v-bind:disabled="!product.canBeRejected"
                            @click="rejectOrder"
                        >
                            <span>
                                {{ trans('products.reject_order.action') }}
                            </span>
                            <spinner v-if="isRejectingOrder" />
                        </ui5-button>
                    </div>
                    <div class="mt-5 flex items-center flex-col">
                        <ui5-button
                            class="border border-primary mb-[3px] hover:text-primary w-full"
                            v-bind:disabled="isSavingAndProcessingProduct"
                            @click="saveAndProcessProduct"
                        >
                            <span>
                                {{
                                    trans(
                                        'cutting_pleating_validation.actions.save_and_process_product'
                                    )
                                }}
                            </span>
                        </ui5-button>
                    </div>
                    <hr class="mt-8 mb-8" />
                    <h2 class="font-bold text-xl mb-2">
                        {{
                            trans(
                                'cutting_pleating_validation.details.sections.note'
                            )
                        }}
                    </h2>
                    <div
                        v-if="product.note"
                        v-html="product.note"
                    />
                </div>
            </div>
        </div>
        <file-select-modal
            :show="imageSelectModal.show"
            accepts-file-types="image/png"
            @confirm="onImageSelected"
            @close="toggleImageSelectModal(false)"
            :loading="imageSelectModal.fileSelectLoading"
            :loading-text="imageSelectModal.fileSelectLoadingText"
            :multiple="false"
        />
        <modal
            v-if="product"
            :show="imageModal.show"
            :loading="imageModal.loading"
            :actions="imageModal.actions"
            @close="toggleImageModal(false)"
            :title="product.productNumber"
        >
            <img
                :src="product.imageSrc"
                :alt="product.productNumber"
                @click="toggleImageModal(false)"
                class="block aspect-video bg-slate-200 cursor-zoom-out max-w-full w-[1200px]"
            />
        </modal>
    </section>
</template>
<script>
import moment from 'moment';
import { saveAs } from 'file-saver';
import cuttingPleatingApi from '../api/cuttingPleating';
import productApi from '../api/product';
import productAttachmentApi from '../api/productAttachment';
import orderApi from '../api/order';
import localization from '../mixins/localization';
import { downloadSingleAttachment, downloadAttachmentsZip } from '../helpers/FileDownloadHelper';
import DateInput from '../components/DateInput';
import TextInput from '../components/TextInput.vue';
import Modal from '../components/modals/Modal';
import FileSelectModal from '../components/modals/FileSelectModal';
import Loader from '../components/loader/Loader';
import Spinner from '../components/loader/Spinner';
import SpinnerText from '../components/loader/SpinnerText';
import {
    showConfirmDialog,
    showErrorMessage,
    showInfoMessage,
} from '../dialogs';
import { createBlobFromImage } from '../helpers/ImageHelpers';
import IsDebugMixin from '../mixins/is-debug';

export default {
    data() {
        return {
            product: null,
            hasValidCustomProductImage: false,
            isLoadingProduct: false,
            form: {
                valueFields: {},
            },
            initialValueFields: {}, // Store initial state for dirty checking
            isStoringValueFields: false,
            isRejectingOrder: false,
            isSavingAndProcessingProduct: false,
            manualUnfoldment: null,
            imageModal: {
                show: false,
                loading: false,
                actions: [],
            },
            imageSelectModal: {
                show: false,
                fileSelectLoading: false,
                fileSelectLoadingText: '',
            },
        };
    },
    watch: {
        '$route.params.id': {
            handler: function () {
                this.fetchDetails();
            },
        },
    },
    computed: {
        productTitle() {
            const quantityLabel = this.trans('cutting_pleating_validation.details.labels.quantity');
            return `${this.product.productNumber} - ${this.product.productTypeLabel} (${quantityLabel}: ${this.product.count})`;
        },
        deliveryTimeLabel() {
            switch (parseInt(this.product.deliveryTimeId)) {
                case 2:
                    return this.trans(
                        'cutting_pleating_validation.details.delivery_times.shortened'
                    );
                case 3:
                    return this.trans(
                        'cutting_pleating_validation.details.delivery_times.express'
                    );
                default:
                    return this.trans(
                        'cutting_pleating_validation.details.delivery_times.standard'
                    );
            }
        },
        deliveryTimeColorClasses() {
            switch (parseInt(this.product.deliveryTimeId)) {
                case 2:
                    return 'text-primary';
                case 3:
                    return 'text-alert font-bold';
                default:
                    return 'text-success';
            }
        },
        deductionValues() {
            return [
                'materialtype',
                'thickness',
                'NrOfFolds',
                'length',
                //'unfoldment',
                'sumsides',
            ]
                .map((dataField) => this.dataFieldByName(dataField))
                .filter((dataField) => dataField)
                .map((dataField) => {
                    const label = this.dataFieldLabel(dataField);
                    const valueField = this.valueField(dataField);
                    let value = valueField?.valuename ?? null;

                    if (!valueField) {
                        console.warn('No valuefield for datafield', dataField);
                    } else {
                        const dataType = dataField.datatype
                            ? dataField.datatype.type
                            : 'string';

                        if (['options', 'popup', 'table'].includes(dataType)) {
                            const option = dataField.options.find(
                                (o) =>
                                    Number(o.value) ===
                                    Number(valueField.valueOption)
                            );

                            if (option) {
                                value = option.label;
                            }
                        }
                    }

                    return {
                        id: dataField.id,
                        label: label,
                        value,
                    };
                });
        },
        sortedDataFields() {
            return this.product.dataFields
                .sort((a, b) => {
                    return Number(a.order) - Number(b.order);
                })
                .filter(
                    (dataField) =>
                        dataField.inUse &&
                        dataField.generalFieldID !== 'reference4'
                );
        },
        isFormDirty() {
            return (
                JSON.stringify(this.form.valueFields) !==
                JSON.stringify(this.initialValueFields)
            );
        },
    },
    methods: {
        moment,
        exportDxfJson() {
            cuttingPleatingApi
                .getDxfJson(this.product.productId, { responseType: 'json' })
                .then((response) => {
                    const jsonText = JSON.stringify(response.data, null, 2);
                    const blob = new Blob([jsonText], {
                        type: 'application/json',
                    });
                    saveAs(blob, `${this.product.productNumber}_dxf.json`);
                })
                .catch((error) => {
                    console.error(error);
                    showErrorMessage(
                        this.trans(
                            'cutting_pleating_validation.errors.download_dxf_json_failed'
                        )
                    );
                });
        },
        copyManualToDataField() {
            const dataField = this.dataFieldByName('unfoldment');
            if (dataField) {
                this.updateFormValueField(
                    dataField,
                    this.manualUnfoldment ?? this.product.calculatedUnfoldment
                );
            }
        },
        getValueFieldValue(dataField) {
            const valueField = this.valueField(dataField);
            return valueField ? `{${valueField.valuename ?? ''}}` : '';
        },
        downloadImage() {
            const imgElement = document.querySelector(
                `img[src="${this.product.imageSrc}"]`
            );

            createBlobFromImage(imgElement)
                .then((blob) => {
                    if (blob) {
                        saveAs(blob, `${this.product.productNumber}.png`);
                    } else {
                        showErrorMessage(
                            this.trans(
                                'cutting_pleating_validation.errors.download_image_failed'
                            )
                        );
                    }
                })
                .catch((error) => {
                    console.error(error);
                    showErrorMessage(
                        this.trans(
                            'cutting_pleating_validation.errors.download_image_failed'
                        )
                    );
                });
        },
        setProductImageSrc(product) {
            product.imageSrc =
                Config.url +
                '/product/customImage/?productId=' +
                product.productId +
                '&random=' +
                Math.random();
        },
        fetchDetails() {
            this.isLoadingProduct = true;

            const { id } = this.$route.params;

            cuttingPleatingApi
                .fetchProductToValidate(id)
                .then((response) => {
                    if (!response.data || !response.data.data) {
                        throw new Error();
                    }

                    const product = response.data.data;

                    this.setProductImageSrc(product);

                    this.product = product;
                    this.updateFormData();
                })
                .catch(() => {
                    showErrorMessage(
                        this.trans(
                            'cutting_pleating_validation.errors.fetching_details'
                        )
                    );
                })
                .finally(() => {
                    this.isLoadingProduct = false;
                });
        },
        dataFieldLabel(dataField) {
            const locale = this.currentLocale();

            const translation = dataField.translations.find(
                (translation) => translation.language === locale
            );

            if (translation) {
                return (
                    translation.externalName ||
                    dataField.externalName ||
                    dataField.name
                );
            }

            return dataField.externalName || dataField.name;
        },
        dataFieldByName(name) {
            if (!this.product) {
                return null;
            }

            const result = this.product.dataFields.find(
                (dataField) => dataField.name === name
            );

            if (this.isDebug) {
                console.log({ name, product: this.product, result });
            }
            return result;
        },
        dataFieldByGeneralFieldId(generalFieldID) {
            if (!this.product) {
                return null;
            }

            return this.product.dataFields.find(
                (DataField) => DataField.generalFieldID === generalFieldID
            );
        },
        getDataFieldIdentifier(dataField) {
            return dataField.generalFieldID !== null
                ? dataField.generalFieldID
                : dataField.id;
        },
        valueField(dataField) {
            if (!dataField || !this.product) {
                return null;
            }

            return (
                this.product.valueFields.find(
                    (valueField) => valueField.dataFieldId === dataField.id
                ) ?? null
            );
        },
        updateFormData() {
            const valueFields = {};

            this.sortedDataFields.forEach((dataField) => {
                const valueField = this.valueField(dataField);
                let value = null;

                if (valueField) {
                    const dataType = dataField.datatype
                        ? dataField.datatype.type
                        : 'string';

                    if (['options', 'popup', 'table'].includes(dataType)) {
                        value = valueField?.valueOption ?? '';
                    } else {
                        value = valueField?.valuename ?? '';
                    }
                }

                valueFields[this.getDataFieldIdentifier(dataField)] = value;
            });

            this.$set(this.form, 'valueFields', valueFields);
            this.$set(
                this,
                'initialValueFields',
                JSON.parse(JSON.stringify(valueFields))
            );
        },
        updateFormValueField(dataField, value) {
            if (this.isDebug) {
                console.log('updateFormValueField', { dataField, value });
            }

            if (
                value == 'null' &&
                dataField.datatype &&
                ['options', 'popup', 'table', 'multiselect'].includes(
                    dataField.datatype.type
                )
            ) {
                value = null;
            }

            this.$set(
                this.form.valueFields,
                this.getDataFieldIdentifier(dataField),
                value
            );
        },
        storeValueFields() {
            this.isStoringValueFields = true;

            const valueFields = this.form.valueFields;
            const productId = this.product.productId;

            productApi
                .storeValueFields(productId, valueFields)
                .then(() => {
                    showInfoMessage(
                        this.trans(
                            'cutting_pleating_validation.feedback.storing_value_fields'
                        )
                    );
                    this.fetchDetails();
                })
                .catch(() => {
                    showErrorMessage(
                        this.trans(
                            'cutting_pleating_validation.errors.storing_value_fields'
                        )
                    );
                })
                .finally(() => {
                    this.isStoringValueFields = false;
                });
        },
        rejectOrder($event) {
            showConfirmDialog({
                title: this.trans('products.reject_order.confirmation.title'),
                content: this.trans(
                    'products.reject_order.confirmation.content'
                ),
                confirm: () => {
                    $event.target.disabled = true;
                    orderApi
                        .rejectOrder(
                            this.product.productOrderId,
                            this.$store.getters['currentUser/getUserId']
                        )
                        .then(() => {
                            showInfoMessage(
                                this.trans('products.reject_order.success')
                            );

                            this.navigateToOverview();
                        })
                        .catch(() => {
                            showErrorMessage(
                                this.trans('products.reject_order.error')
                            );
                        })
                        .finally(() => {
                            $event.target.disabled = false;
                        });
                },
                closeOnCancel: true,
                closeOnOk: true,
                confirmButton: this.trans(
                    'products.reject_order.confirmation.actions.confirm'
                ),
                cancelButton: this.trans(
                    'products.reject_order.confirmation.actions.cancel'
                ),
            });
        },
        navigateToOverview() {
            this.$router.push('/validate-cutting-pleating');
        },
        navigateToNextProduct() {
            if (!this.product || !this.product.nextProductId) {
                return this.navigateToOverview();
            }

            this.$router.push(
                `/validate-cutting-pleating/${this.product.nextProductId}`
            );
        },
        navigateToPreviousProduct() {
            if (!this.product || !this.product.previousProductId) {
                return this.navigateToOverview();
            }

            this.$router.push(
                `/validate-cutting-pleating/${this.product.previousProductId}`
            );
        },
        saveAndProcessProduct() {
            this.isSavingAndProcessingProduct = true;

            cuttingPleatingApi
                .saveAndProcessProduct(
                    this.product.productId,
                    this.$store.getters['currentUser/getUserId'],
                    this.manualUnfoldment
                )
                .then(() => {
                    showInfoMessage(
                        this.trans(
                            'cutting_pleating_validation.feedback.saving_and_processing_product_success'
                        )
                    );

                    this.navigateToNextProduct();
                })
                .catch((error) => {
                    console.log(error);

                    showErrorMessage(
                        this.trans(
                            'cutting_pleating_validation.errors.saving_and_processing_product'
                        )
                    );
                })
                .finally(() => {
                    this.isSavingAndProcessingProduct = false;
                });
        },

        toggleImageModal(show = true) {
            this.$set(this.imageModal, 'show', show);
        },

        toggleImageSelectModal(show = true) {
            this.$set(this.imageSelectModal, 'show', show);
        },

        onImageSelected(files) {
            if (files.length === 0) {
                return;
            }

            if (files.length > 1) {
                console.warn(
                    "More than one file, shouldn't be possible to happen"
                );
            }

            this.imageSelectModal.fileSelectLoadingText = this.trans(
                'cutting_pleating_validation.upload_custom_image.uploading_image'
            );
            this.imageSelectModal.fileSelectLoading = true;
            productApi
                .uploadProductImage(files[0], this.product.productId)
                .then((response) => {
                    console.log(response.data.imagePathS3);
                    this.setProductImageSrc(this.product);
                    this.toggleImageSelectModal(false);
                })
                .catch((error) => {
                    console.error(error);

                    const status = error.response?.status;
                    if (status === 400) {
                        const data = error.response?.data;
                        const maxSize = data.maxSize;

                        showErrorMessage(
                            this.trans(
                                'cutting_pleating_validation.upload_custom_image.max_size',
                                { maxSize }
                            )
                        );
                    } else {
                        this.showDefaultErrorMessage(status);
                    }
                })
                .finally(() => {
                    this.imageSelectModal.fileSelectLoadingText = '';
                    this.imageSelectModal.fileSelectLoading = false;
                });
        },

        downloadAttachment(attachment) {
            downloadSingleAttachment(
                attachment.id,
                attachment.fileName,
                this.$store.getters['currentUser/getToken'],
                this.trans('cutting_pleating_validation.errors.download_attachment_failed')
            );
        },

        downloadAllProductAttachments() {
            downloadAttachmentsZip(
                () => productAttachmentApi.fetchByProductId(this.product.productId),
                `attachments-${this.product.productNumber}`,
                {
                    error: this.trans('cutting_pleating_validation.errors.download_attachment_failed')
                }
            );
        },
    },
    mounted() {
        this.fetchDetails();
    },
    components: {
        DateInput,
        Loader,
        Modal,
        FileSelectModal,
        Spinner,
        SpinnerText,
        TextInput,
    },
    mixins: [localization, IsDebugMixin],
};
</script>
